import { useState, useEffect } from 'react';
import merchantOrdersService from '../../services/merchantorders.service';
import { getStoreLabel } from '../../constants/stores';

export default function ConsolidationView() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const allOrders = await merchantOrdersService.getAllPendingOrders();
      // Sort by creation date (newest first)
      const sorted = (allOrders || []).sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA;
      });
      setOrders(sorted);
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkOrdered = async (orderId) => {
    if (!window.confirm('Marcar esta orden como ordenada?')) return;
    try {
      await merchantOrdersService.updateOrder(orderId, { status: 'ordered' });
      await loadData();
    } catch (error) {
      console.error('Error marking ordered:', error);
      alert('Error al actualizar la orden. Intenta de nuevo.');
    }
  };

  const handleMarkReceived = async (orderId) => {
    if (!window.confirm('Marcar esta orden como recibida?')) return;
    try {
      await merchantOrdersService.markReceived(orderId);
      await loadData();
    } catch (error) {
      console.error('Error marking received:', error);
      alert('Error al actualizar la orden. Intenta de nuevo.');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-MX', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Aggregate product totals for all unsent orders
  const getUnsentSummary = () => {
    const itemMap = new Map();
    orders.forEach(order => {
      if (order.status === 'pending') {
        order.items.forEach(item => {
          const key = item.productId || item.name;
          if (itemMap.has(key)) {
            itemMap.get(key).quantity += item.quantity;
          } else {
            itemMap.set(key, {
              name: item.name,
              quantity: item.quantity
            });
          }
        });
      }
    });
    return Array.from(itemMap.values());
  };

  if (loading) {
    return (
      <div className="merchant-panel">
        <p>Cargando...</p>
      </div>
    );
  }

  const unsentSummary = getUnsentSummary();
  const unsentCount = orders.filter(o => o.status === 'pending').length;

  return (
    <div className="merchant-panel">
      <div className="merchant-panel-header">
        <h2>Pedidos Central</h2>
      </div>

      {/* Unsent Orders Summary */}
      {unsentCount > 0 && (
        <div className="consolidation-group" style={{ marginBottom: '1.5rem' }}>
          <div className="consolidation-group-header">
            <h3>Resumen de Pedidos Pendientes</h3>
            <span>{unsentCount} pedido(s)</span>
          </div>
          <div className="consolidation-summary">
            {unsentSummary.map((item, i) => (
              <div key={i} className="consolidation-summary-card">
                <strong>{item.quantity}</strong>
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="empty-state">
          <h3>No hay pedidos pendientes</h3>
          <p>Todos los pedidos han sido procesados.</p>
        </div>
      ) : (
        <div className="orders-table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tienda</th>
                <th>Proveedor</th>
                <th>Articulos</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>{getStoreLabel(order.store_id)}</td>
                  <td>{order.merchantName}</td>
                  <td>
                    {order.items.map((item, i) => (
                      <div key={i} style={{ fontSize: '0.85rem' }}>
                        {item.quantity}x {item.name}
                      </div>
                    ))}
                  </td>
                  <td>{formatPrice(order.total)}</td>
                  <td>
                    <span className={`status-badge ${order.status}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {order.status === 'pending' && (
                        <button
                          className="action-button"
                          onClick={() => handleMarkOrdered(order.id)}
                        >
                          Marcar Ordenado
                        </button>
                      )}
                      {order.status === 'ordered' && (
                        <button
                          className="action-button"
                          onClick={() => handleMarkReceived(order.id)}
                        >
                          Marcar Recibido
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
